import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  HIGHLIGHT_CARDS,
  HighlightCardType,
} from "domains/home/constants/highlight";
import { useCreateNewModelModalContext } from "domains/models/contexts/CreateNewModelModalProvider";
import { useChatContext } from "domains/navigation/contexts/ChatProvider";
import Button from "domains/ui/components/Button";
import { CarouselNavButton } from "domains/ui/components/Carousel";
import { useUserContext } from "domains/user/contexts/UserProvider";
import useEmblaCarousel from "embla-carousel-react";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useIntl } from "react-intl";

import {
  Box,
  Center,
  Flex,
  HStack,
  Image as ChakraImage,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";

import { rewriteUrl } from "./helpers";

export default function SectionHighlights() {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { isUnsigned } = useUserContext();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    skipSnaps: true,
    loop: false,
  });

  // ----------------------------------

  const updateCurrentIndex = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  /**
   * Compute visible card slugs to help identify when a user clicks on the carousel navigation
   * buttons.
   *
   * Used by Track() function
   */
  const getVisibleCardSlugs = useCallback(() => {
    if (!emblaApi) return [];
    const visibleCardIds = emblaApi.slidesInView();
    return HIGHLIGHT_CARDS.flatMap((card, index) =>
      visibleCardIds.includes(index) ? [card.slug] : []
    );
  }, [emblaApi]);

  const getCurrentCard = useCallback(() => {
    if (!emblaApi) return undefined;
    const currentIndex = emblaApi.selectedScrollSnap();
    return HIGHLIGHT_CARDS[currentIndex];
  }, [emblaApi]);

  const scroll = useCallback(
    (direction: "next" | "prev") => {
      Track(AnalyticsEvents.HomeCarousel.ClickedCarouselNavigation, {
        userIsAuthenticated: !isUnsigned,
        direction,
        currentCardSlug: getCurrentCard()?.slug,
        visibleCardSlugs: getVisibleCardSlugs(),
      });

      if (!emblaApi) return;
      const currentIndex = emblaApi.selectedScrollSnap();
      const moveDistance = 1;
      const nextIndex =
        direction === "next"
          ? currentIndex + moveDistance
          : currentIndex - moveDistance;
      const finalIndex =
        // We are at the end of the carousel
        nextIndex > HIGHLIGHT_CARDS.length - 1
          ? 0
          : // We are at the beginning of the carousel
          nextIndex < 0
          ? HIGHLIGHT_CARDS.length - 1
          : // We are in the middle of the carousel
            nextIndex;
      emblaApi.scrollTo(finalIndex);
    },
    [emblaApi, getCurrentCard, getVisibleCardSlugs, isUnsigned]
  );

  // ----------------------------------

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", updateCurrentIndex);
    return () => {
      emblaApi.off("select", updateCurrentIndex);
    };
  }, [emblaApi, updateCurrentIndex]);

  // ----------------------------------

  const handleCardCtaClick = useCallback(
    (props: {
      slug: string;
      tag?: string;
      title: string;
      description: string;
      background?: string;
      video?: string;
      callToActionLabel: string;
      cardPosition: number;
    }) => {
      Track(AnalyticsEvents.HomeCarousel.ClickedCarouselCardCTA, {
        userIsAuthenticated: !isUnsigned,
        persona: "default",
        description: props.description,
        cardSlug: props.slug,
        currentCardSlug: props.slug,
        cardTag: props.tag,
        cardTitle: props.title,
        mediaType: props.video ? "video" : "image",
        mediaUrl: props.video ? props.video : props.background,
        ctaLabel: props.callToActionLabel,
        cardPosition: props.cardPosition,
        visibleCardSlugs: getVisibleCardSlugs(),
      });
    },
    [getVisibleCardSlugs, isUnsigned]
  );

  // ----------------------------------

  return (
    <Box>
      <Box ref={emblaRef} pos="relative" overflow="visible" mx={-9} px={9}>
        <Flex
          sx={{
            backfaceVisibility: "hidden",
            touchAction: "pan-y",
          }}
          // This margin right helps handling the last carousel card selection with embla and enable
          // Scrolling to the beginning of the carousel
          mr={{
            base: "20px",
            md: "50px",
            lg: "100px",
          }}
          ml={-8}
        >
          {HIGHLIGHT_CARDS.map((card, index) => (
            <HighlightCard
              key={`highlight_card_${index}`}
              {...card}
              cardPosition={index + 1}
              onCardCtaClick={handleCardCtaClick}
            />
          ))}
        </Flex>
      </Box>

      <HStack align="center" maxW="1370px" mt={2} mx="auto" spacing={2}>
        <Box w="75px" />
        <Center flex={1} gap={0}>
          {HIGHLIGHT_CARDS.map((_content, idx) => (
            <Center
              key={`indicator-${idx}`}
              p={1.5}
              borderRadius="50%"
              _hover={{ bgColor: "whiteAlpha.50" }}
              cursor="pointer"
              data-group
              onClick={() => emblaApi?.scrollTo(idx)}
            >
              <Box
                w="6px"
                h="6px"
                bg={currentIndex === idx ? "textPrimary" : "whiteAlpha.300"}
                borderRadius="full"
                _groupHover={{
                  bg: currentIndex === idx ? "textPrimary" : "whiteAlpha.600",
                }}
              />
            </Center>
          ))}
        </Center>

        <HStack align="center" w="75px" spacing={2}>
          <CarouselNavButton
            size="md"
            variant="secondary"
            direction="prev"
            onClick={() => scroll("prev")}
          />
          <CarouselNavButton
            size="md"
            variant="secondary"
            direction="next"
            onClick={() => scroll("next")}
          />
        </HStack>
      </HStack>
    </Box>
  );
}

// ------------------------------------

interface HighlightCardProps extends HighlightCardType {
  cardPosition: number;
  onCardCtaClick: (props: {
    slug: string;
    tag?: string;
    title: string;
    description: string;
    background?: string;
    video?: string;
    callToActionLabel: string;
    cardPosition: number;
  }) => void;
}

function HighlightCard({
  altText,
  altIntl,
  tag,
  title,
  description,
  background,
  video,
  callToAction,
  slug,
  cardPosition,
  onCardCtaClick,
}: HighlightCardProps) {
  const { isUnsigned, openSignUpModal } = useUserContext();
  const { setIsChatVisible } = useChatContext();
  const { open: openCreateModelModal } = useCreateNewModelModalContext();
  const intl = useIntl();

  const handleCallToActionClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      onCardCtaClick({
        slug,
        tag,
        title,
        description,
        background,
        video,
        callToActionLabel: callToAction.label,
        cardPosition,
      });
      if (isUnsigned && callToAction.signedVariant) {
        e.preventDefault();
        openSignUpModal(callToAction.signedVariant);
      } else if (callToAction.onClick === "openChat") {
        setIsChatVisible(true);
      } else if (callToAction.onClick === "openCreateModelModal") {
        openCreateModelModal();
      }
    },
    [
      background,
      callToAction.label,
      callToAction.onClick,
      callToAction.signedVariant,
      cardPosition,
      description,
      isUnsigned,
      onCardCtaClick,
      openCreateModelModal,
      openSignUpModal,
      setIsChatVisible,
      slug,
      tag,
      title,
      video,
    ]
  );

  /**
   * Detect if the browser supports webp images
   */
  const isWebpSupported = useMemo(() => {
    const img = new Image();
    img.src =
      "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAQAcJaQAA3AA/v3AgAA=";
    return img.width > 0;
  }, []);

  /**
   * Detect window size and return the most optimized version of the background or video
   */
  const optimizedMediaSize = useMemo((): string => {
    if (window.innerWidth < 768) {
      return "380";
    }
    return "640";
  }, []);

  /**
   * Rewrite the background or video url to the most optimized version
   *
   * return the maybe rewritten url
   *
   * @note temporary solution to optimize the homepage loading time, I would prefer to leverage the
   *       `next/image` component to handle the image optimization, but it seems a bit more complex
   *       than expected to implement.
   */
  const { background: optimizedBackground, video: optimizedVideo } =
    useMemo(() => {
      return {
        background: rewriteUrl({
          url: background,
          optimizedMediaSize: optimizedMediaSize,
          webpSupport: isWebpSupported,
        }),
        video: rewriteUrl({
          url: video,
          optimizedMediaSize: optimizedMediaSize,
          webpSupport: isWebpSupported,
        }),
      };
    }, [background, isWebpSupported, optimizedMediaSize, video]);

  return (
    <Box minW={["100%", "100%", "auto", "auto"]} pl={8} userSelect="none">
      <Box
        sx={{
          WebkitMaskImage:
            "radial-gradient(circle at center, white 100%, transparent 100%)",
          maskImage:
            "radial-gradient(circle at center, white 100%, transparent 100%)",
        }}
        pos="relative"
        overflow="hidden"
        w={["100%", "100%", "450px", "600px"]}
        bgSize="cover"
        borderRadius="xl"
      >
        <Box pb="52%" />

        <ChakraImage
          pos="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          w="100%"
          h="100%"
          objectFit="cover"
          alt={intl.formatMessage({
            id: altIntl,
            defaultMessage: altText,
          })}
          src={optimizedBackground}
        />

        {video && (
          <Box
            pos="absolute"
            top={0}
            right={0}
            bottom={0}
            left={0}
            overflow="hidden"
            borderRadius="inherit"
          >
            <video
              width="100%"
              height="100%"
              muted
              poster={optimizedBackground}
              autoPlay
              loop
              aria-label={intl.formatMessage({
                id: altIntl,
                defaultMessage: altText,
              })}
              style={{
                objectFit: "cover",
                objectPosition: "center",
                width: "100%",
                height: "100%",
                borderRadius: "inherit",
              }}
              playsInline
            >
              <source src={optimizedVideo} type="video/mp4" />
            </video>
          </Box>
        )}

        <Box
          pos="absolute"
          top="-1px"
          right="-1px"
          bottom="-1px"
          left="-1px"
          bg="linear-gradient(180deg, rgba(0, 0, 0, 0) 28.97%, rgba(0, 0, 0, 0.8) 74.61%)"
        />

        {!!tag && (
          <Box pos="absolute" top={0} right={0} left={0} p={6}>
            <Tag fontWeight={500} bgColor="blackAlpha.500" variant="subtle">
              {tag}
            </Tag>
          </Box>
        )}

        <HStack
          pos="absolute"
          right={0}
          bottom={0}
          left={0}
          align="flex-end"
          p={6}
          spacing={4}
        >
          <VStack align="stretch" flex={1} overflow="hidden" spacing={1}>
            <Text
              color="textPrimary"
              lineHeight="1.2"
              isTruncated
              size={["title.md", "title.md", "title.md", "title.lg"]}
            >
              {title}
            </Text>

            <Text
              sx={{
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
              display="-webkit-box"
              overflow="hidden"
              color="textTertiary"
              size="body.md"
            >
              <span dangerouslySetInnerHTML={{ __html: description }} />
            </Text>
          </VStack>

          <Box flex={0}>
            <Button
              variant="primary"
              colorScheme="white"
              size="sm"
              borderRadius="18px"
              externalLink={callToAction.externalLink}
              internalLink={callToAction.internalLink}
              onClick={handleCallToActionClick}
            >
              {callToAction.label}
            </Button>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
}
